import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, absUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  PlusCircle,
  Film,
  Rocket,
  Calendar,
  UserCircle2,
  CheckCircle2,
  Circle,
  ArrowRight,
  Sparkles,
  Hourglass,
  Wand2,
  Activity,
} from "lucide-react";

const TILES = [
  { key: "total_videos", label: "Total Videos", Icon: Film },
  { key: "ready_to_publish", label: "Ready to Publish", Icon: Rocket },
  { key: "this_week", label: "This Week", Icon: Calendar },
  {
    key: "awaiting_retry",
    label: "Awaiting Retry",
    Icon: Hourglass,
    linkTo: "/library?awaiting_retry=1",
    accent: (v) => (v > 0 ? "amber" : null),
  },
  {
    key: "animation_quality_pct",
    label: "Animated (30d)",
    Icon: Wand2,
    suffix: "%",
    accent: (v) => (v < 90 ? "amber" : "emerald"),
  },
  { key: "active_character_name", label: "Active Character", Icon: UserCircle2, isText: true },
];

const TYPE_COLORS = {
  story: "bg-amber-100 text-amber-800",
  music: "bg-rose-100 text-rose-800",
  learning: "bg-emerald-100 text-emerald-800",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [credits, setCredits] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api
      .get("/credits/health")
      .then((r) => setCredits(r.data))
      .catch(() => {});
    api
      .get("/system/health")
      .then((r) => setSystemHealth(r.data))
      .catch(() => {});
  }, []);

  const onboarding = stats?.onboarding || [];
  const recent = stats?.recent_videos || [];

  return (
    <div data-testid="page-dashboard" className="mx-auto max-w-6xl space-y-8">
      {/* Hero */}
      <section className="rounded-2xl border border-orange-200 bg-gradient-to-br from-amber-100 via-orange-100 to-rose-100 p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-stone-800 sm:text-4xl">
              Welcome back!
            </h1>
            <p className="mt-2 max-w-xl text-stone-700">
              Let&apos;s make some magic for{" "}
              <span className="font-semibold text-orange-700">
                Dom&apos;s Journey
              </span>{" "}
              <Sparkles className="inline h-4 w-4 -translate-y-0.5 text-amber-500" />
            </p>
          </div>
          <Button
            asChild
            data-testid="dashboard-create-btn"
            className="h-11 rounded-full bg-gradient-to-r from-orange-500 to-rose-500 px-6 text-white shadow-md hover:from-orange-600 hover:to-rose-600"
          >
            <Link to="/create">
              <PlusCircle className="mr-2 h-5 w-5" />
              Create New Video
            </Link>
          </Button>
        </div>
      </section>

      {/* API Health banner (Phase 4.5) */}
      {credits ? (
        <Link
          to="/settings#credits"
          data-testid="api-health-tile"
          className={`block rounded-xl border px-4 py-3 text-sm transition hover:shadow-md ${
            credits.overall === "critical"
              ? "border-rose-300 bg-rose-50 text-rose-900"
              : credits.overall === "low"
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : "border-emerald-300 bg-emerald-50 text-emerald-900"
          }`}
        >
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5" />
            <div className="flex-1">
              <div className="font-semibold">
                API Health:{" "}
                {credits.overall === "critical"
                  ? "🔴 Top up needed"
                  : credits.overall === "low"
                  ? "🟡 1+ providers running low"
                  : "🟢 All systems healthy"}
              </div>
              <div className="text-xs">
                {credits.critical_providers?.length > 0
                  ? `Critical: ${credits.critical_providers.join(", ")} — generation will queue retry until topped up.`
                  : credits.low_providers?.length > 0
                  ? `Running low: ${credits.low_providers.join(", ")}`
                  : "Click to view balances → Settings"}
              </div>
            </div>
            <ArrowRight className="h-4 w-4 opacity-60" />
          </div>
        </Link>
      ) : null}

      {/* Phase 5: Backlog Progress */}
      {stats ? (
        <div
          data-testid="backlog-tile"
          className={`rounded-xl border p-4 ${
            stats.backlog_count >= stats.backlog_target
              ? "border-emerald-300 bg-emerald-50/70"
              : "border-amber-200 bg-amber-50/60"
          }`}
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Backlog Progress
              </div>
              <div className="text-2xl font-bold text-stone-800">
                {stats.backlog_count} / {stats.backlog_target} videos
              </div>
              <div className="mt-1 text-xs text-stone-700">
                {stats.backlog_count >= stats.backlog_target
                  ? "🚀 Backlog complete! Ready to launch — bulk-flip your library to Public."
                  : "🛠️ Building backlog. Keep videos Unlisted. YouTube algorithm rewards channels with depth."}
              </div>
            </div>
            <Link
              to={
                stats.backlog_count >= stats.backlog_target
                  ? "/library"
                  : "/presets"
              }
              data-testid="backlog-cta"
              className="rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600"
            >
              {stats.backlog_count >= stats.backlog_target
                ? "Review & Publish"
                : "Create Series"}
              <ArrowRight className="ml-1 inline h-3 w-3" />
            </Link>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-stone-200">
            <div
              className={`h-full transition-all ${
                stats.backlog_count >= stats.backlog_target
                  ? "bg-emerald-500"
                  : "bg-amber-500"
              }`}
              style={{ width: `${stats.backlog_pct}%` }}
            />
          </div>
        </div>
      ) : null}

      {/* Phase 5: System Health pill */}
      {systemHealth ? (
        <Link
          to="/settings"
          data-testid="system-health-tile"
          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-xs ${
            systemHealth.ok
              ? "border-emerald-200 bg-emerald-50/40 text-emerald-900"
              : "border-rose-300 bg-rose-50 text-rose-900"
          }`}
        >
          <span className="flex items-center gap-2">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                systemHealth.ok ? "bg-emerald-500" : "bg-rose-500"
              }`}
            />
            <strong>System health:</strong>{" "}
            {systemHealth.ok ? "all checks passed" : "issues detected"}
            {systemHealth.checks?.stuck_videos?.bumped_count > 0 ? (
              <span>
                · auto-recovered{" "}
                {systemHealth.checks.stuck_videos.bumped_count} stuck video(s)
              </span>
            ) : null}
            {systemHealth.checks?.providers?.flips?.length > 0 ? (
              <span className="text-rose-800">
                · provider regression on{" "}
                {systemHealth.checks.providers.flips
                  .map((f) => f.provider)
                  .join(", ")}
              </span>
            ) : null}
          </span>
          <span className="opacity-60">
            last check: {systemHealth.elapsed_ms ?? "?"}ms
          </span>
        </Link>
      ) : null}

      {/* Stats tiles */}
      <section
        data-testid="stats-tiles"
        className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6"
      >
        {TILES.map(({ key, label, Icon, isText, suffix, linkTo, accent }) => {
          const raw = stats ? stats[key] : null;
          const display = isText
            ? raw || "—"
            : typeof raw === "number"
            ? `${raw}${suffix || ""}`
            : "—";
          const accentColor = accent && typeof raw === "number" ? accent(raw) : null;
          const accentClass =
            accentColor === "amber"
              ? "border-amber-300 bg-amber-50/80"
              : accentColor === "emerald"
              ? "border-emerald-200 bg-emerald-50/60"
              : "border-orange-200 bg-white/80";
          const inner = (
            <Card
              data-testid={`stat-${key}`}
              className={`shadow-sm transition ${accentClass} ${
                linkTo ? "hover:shadow-md" : ""
              }`}
            >
              <CardContent className="flex items-center gap-4 p-5">
                <div
                  className={`flex h-11 w-11 items-center justify-center rounded-xl text-orange-700 ${
                    accentColor === "amber"
                      ? "bg-amber-200 text-amber-800"
                      : accentColor === "emerald"
                      ? "bg-emerald-200 text-emerald-800"
                      : "bg-gradient-to-br from-orange-100 to-rose-100"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-stone-500">
                    {label}
                  </div>
                  <div className="text-2xl font-bold text-stone-800">
                    {display}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          return linkTo ? (
            <Link key={key} to={linkTo}>
              {inner}
            </Link>
          ) : (
            <div key={key}>{inner}</div>
          );
        })}
      </section>

      {/* Onboarding checklist */}
      {stats && onboarding.some((o) => !o.done) ? (
        <Card
          data-testid="onboarding-card"
          className="border-orange-200 bg-white/80 shadow-sm"
        >
          <CardHeader>
            <CardTitle className="text-stone-800">Get started</CardTitle>
            <CardDescription>
              A few quick setup steps and you&apos;ll be publishing episodes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {onboarding.map((o) => (
              <div
                key={o.key}
                data-testid={`onboard-${o.key}`}
                className="flex items-start gap-3 rounded-lg border border-orange-100 bg-white px-4 py-3"
              >
                {o.done ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                ) : (
                  <Circle className="mt-0.5 h-5 w-5 shrink-0 text-stone-400" />
                )}
                <div className="flex-1">
                  <div className="font-medium text-stone-800">{o.label}</div>
                  <div className="text-xs text-stone-500">{o.hint}</div>
                </div>
                {!o.done ? (
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="border-orange-300 text-stone-700"
                  >
                    <Link
                      to={
                        o.key === "character"
                          ? "/characters"
                          : o.key === "first_video"
                          ? "/create"
                          : "/settings"
                      }
                    >
                      Go <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </Button>
                ) : null}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* Recent activity */}
      <Card className="border-orange-200 bg-white/80 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-stone-800">Recent activity</CardTitle>
              <CardDescription>Latest videos in your library</CardDescription>
            </div>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-orange-300 text-stone-700"
            >
              <Link to="/library">View all</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div
              data-testid="recent-empty"
              className="rounded-lg border border-dashed border-orange-200 bg-amber-50 px-6 py-12 text-center text-sm text-stone-600"
            >
              No videos yet — hit <strong>Create New Video</strong> to start.
            </div>
          ) : (
            <ul className="divide-y divide-orange-100">
              {recent.map((v) => (
                <li
                  key={v.id}
                  data-testid={`recent-${v.id}`}
                  className="flex items-center gap-4 py-3"
                >
                  <div className="h-12 w-20 shrink-0 overflow-hidden rounded-md bg-gradient-to-br from-amber-100 to-orange-100">
                    {v.thumbnail_url ? (
                      <img
                        src={absUrl(v.thumbnail_url)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-stone-800">
                      {v.title}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-stone-500">
                      <Badge
                        variant="secondary"
                        className={TYPE_COLORS[v.type] || ""}
                      >
                        {v.type}
                      </Badge>
                      <span>{new Date(v.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <Badge
                    className={
                      v.status === "ready"
                        ? "bg-emerald-600 hover:bg-emerald-600"
                        : v.status === "failed"
                        ? "bg-red-600 hover:bg-red-600"
                        : "bg-amber-500 hover:bg-amber-500"
                    }
                  >
                    {v.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
